<template>
  <div v-bem>
    <FormulateForm
      #default="{ isLoading }"
      v-model="formValues"
      name="wizard"
      keep-model-data
      @submit="handleSubmit"
    >
      <div v-show="$screen.md && !submitted" v-bem:pager>
        Schritt {{ step + 1 }} / {{ wizardSteps.length }}
      </div>
      <div v-bem:content :style="{ height: `${currentStepHeight}px` }">
        <slot v-bind="formContext" />
      </div>
      <div v-if="error != ''" v-bem:error class="pt-4 pb-4 text-center">
        <small>Leider ist ein Fehler aufgetreten:</small>
        <div class="mt-2" v-text="error"></div>
      </div>
      <div v-show="!submitted" v-bem:actions>
        <button type="submit" class="btn btn-primary" :disabled="isLoading">
          {{ isLastStep ? submitButtonLabel : nextButtonLabel }}
        </button>
        <button
          v-if="canGoBack"
          type="button"
          class="btn btn-secondary btn--responsive"
          @click="goBack"
        >
          <span class="d-none d-md-block">{{ backButtonLabel }}</span>
          <i class="d-block d-md-none icon--arrow-left"></i>
        </button>
      </div>
    </FormulateForm>
  </div>
</template>

<script>
import { forEach, get, isArray } from 'lodash-es';
import * as commonOptionSets from './common-optionsets';
import './setup-formulate';

export default {
  name: 'Wizard',
  inject: ['wizardContext'],
  data() {
    return {
      step: null,
      steps: [],
      wizardSteps: [],
      formValues: this.deFlattenedInitialValues(),
      currentStepHeight: 0,
      direction: 'forward',
      submitted: false,
      error: '',
    };
  },
  computed: {
    submitButtonLabel() {
      return this.wizardContext.submitButtonLabel || 'Senden';
    },
    nextButtonLabel() {
      return this.wizardContext.nextButtonLabel || 'Weiter';
    },
    backButtonLabel() {
      return this.wizardContext.backButtonLabel || 'Zurück';
    },
    additionalOptionSets() {
      return this.wizardContext.additionalOptionSets || {};
    },
    currentStep() {
      return this.steps[this.step];
    },
    canGoBack() {
      return this.step > 0;
    },
    isLastStep() {
      return this.step === this.wizardSteps.length - 1;
    },
    flattenedFormValues() {
      return this.flattenValues(this.formValues);
    },
    formContext() {
      return {
        values: this.flattenedFormValues,
        getValue: (key, defaultValue) => {
          return get(this.flattenedFormValues, key, defaultValue);
        },
        getOptionSet: (name) => {
          const optionSets = {
            ...commonOptionSets,
            ...this.additionalOptionSets,
          };

          if (!optionSets[name]) {
            throw new Error(
              `Optionset "${name}" is not found in common option sets.`
            );
          }
          return optionSets[name];
        },
        getOptionSetValue: (optionSetName, valueKey, defaultValue = '') => {
          const options = this.formContext.getOptionSet(optionSetName);
          const value = this.formContext.getValue(valueKey);

          if (isArray(value)) {
            return value.map((key) => options[key]);
          }

          return options[value] || defaultValue;
        },
      };
    },
  },
  watch: {
    step(newValue, oldValue) {
      // Setting direction to initial will do nothing as the animation relating
      // to this is not defined.
      let direction = 'initial';

      // If the previous step is a number we know we are coming from another
      // slide which means this is not the first render.
      if (typeof oldValue === 'number') {
        direction = newValue > oldValue ? 'forward' : 'backward';
      }

      this.direction = direction;

      // To let the direction propagate through all components we need to wait
      // for the next tick. This ensures all children have the most recent data.
      this.$nextTick(() => {
        if (this.steps[oldValue]) {
          this.steps[oldValue].isActive = false;
        }

        if (this.steps[newValue]) {
          this.steps[newValue].isActive = true;
        }

        if (typeof this.wizardContext.onStepChange === 'function') {
          this.wizardContext.onStepChange({
            toStep: newValue,
            fromStep: oldValue,
          });
        }
      });
    },
    submitted(newValue) {
      if (newValue) {
        this.step = this.steps.length - 1;
      }
    },
  },
  mounted() {
    this.step = 0;
  },
  provide() {
    return {
      registerStep: (node) => {
        if (!node.$props.confirmation) {
          this.wizardSteps.push(node);
        }
        this.steps.push(node);
      },
      setCurrentStepHeight: (height) => {
        this.currentStepHeight = height;
      },
      getDirection: () => this.direction,
      formContext: this.formContext,
    };
  },
  methods: {
    flattenValues(values) {
      const flattenedValues = {};
      forEach(values, (groupValue, groupName) => {
        flattenedValues[groupName] = isArray(groupValue)
          ? { ...groupValue[0] }
          : { ...groupValue };
      });
      return flattenedValues;
    },
    deFlattenedInitialValues() {
      const deFlattenedValues = {};
      forEach(this.wizardContext.initialData, (groupValue, groupName) => {
        deFlattenedValues[groupName] = isArray(groupValue)
          ? groupValue
          : [groupValue];
      });
      return deFlattenedValues;
    },
    async handleSubmit(data) {
      this.error = '';
      if (!this.isLastStep) {
        this.step += 1;
      } else if (typeof this.wizardContext.onSubmit === 'function') {
        return new Promise((resolve) => {
          this.wizardContext
            .onSubmit({
              data: this.flattenValues(data),
            })
            .then((submitResponse) => {
              this.submitted = true;
              resolve(submitResponse);
            })
            .catch((error) => {
              console.warn(`wizarderror: ${error}`);
              this.error = error;
              resolve(error);
            });
        });
      }
      return Promise.resolve();
    },
    goForward() {
      this.step = Math.min(this.step + 1, this.steps.length - 1);
      this.error = '';
    },
    goBack() {
      this.step = Math.max(0, this.step - 1);
      this.error = '';
    },
  },
};
</script>

<style lang="scss" scoped>
@use '~tokens/sass/utils';
@use "sass:map";

.wizard {
  position: relative;
  background-color: map.get(utils.$bs-colors, 'white');
  box-shadow: 0 1px 4px 0 rgb(0 0 0 / 15%);
  border-radius: 5px;
  line-height: 1;

  @at-root #{&}__content {
    position: relative;
    height: 500px;
    overflow: hidden;
    transition: height 450ms;
  }

  @at-root #{&}__actions {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    padding: utils.f-rem-calc(10) utils.f-rem-calc(15);
    border-top: 1px solid #d0d7dd;

    @include utils.bs-media-breakpoint-up(md) {
      padding: utils.f-rem-calc(20) utils.f-rem-calc(utils.$v-wizard-gap-x);
    }
  }

  @at-root #{&}__pager {
    position: absolute;
    top: utils.f-rem-calc(utils.$v-wizard-gap-y);
    right: utils.f-rem-calc(utils.$v-wizard-gap-x);
    font-size: utils.f-rem-calc(12);
    color: map.get(utils.$v-colors, 'rw-grey-04');
  }

  @at-root #{&}__error {
    background: #cc071e22;
  }
}
</style>
